import type { UserOfficeAffiliation } from "le-coffre-resources/dist/Notary";
import type { PermissionContextDto } from "@Front/Api/LeCoffreApi/SuperAdmin/RolePermissions/RolePermissions";
import {
	firstResolvedMenuIdentity,
	tryActiveThenPrimaryOfficeIdentity,
	tryIdentityFromPreferredRealOffices,
	tryRealActiveOfficeIdentity,
	type NotaryMenuOfficeIdentity,
} from "./resolveNotaryMenuOfficeIdentityHelpers";

export type { NotaryMenuOfficeIdentity };

/**
 * Aligné sur le sélecteur d’office (`resolvePreferredRealOfficeUid` / office réel mis en avant quand la vue dossiers est invités).
 * Couple (uid + rôle affiliation) pour les garde-fous candidats menu ; l’évaluation matrice « Espace office » réutilise
 * le même uid via `useOfficeMenuMatrixCan` (Header/hooks), pas le sentinel GUEST_FOLDERS du store.
 */
export function resolveNotaryMenuOfficeIdentity(params: {
	activeOffice: UserOfficeAffiliation | null;
	availableOffices: UserOfficeAffiliation[];
	lastRealOfficeUidBeforeGuest: string | null;
	permissionContext: PermissionContextDto | null | undefined;
}): NotaryMenuOfficeIdentity {
	const storeRawOfficeUid = params.activeOffice?.office_uid ?? params.activeOffice?.uid ?? "";
	const ctx = params.permissionContext;

	function stepRealOffice(): NotaryMenuOfficeIdentity | null {
		return tryRealActiveOfficeIdentity(params.activeOffice, storeRawOfficeUid);
	}
	function stepPreferredOffice(): NotaryMenuOfficeIdentity | null {
		return tryIdentityFromPreferredRealOffices({
			availableOffices: params.availableOffices,
			lastRealOfficeUidBeforeGuest: params.lastRealOfficeUidBeforeGuest,
			permissionContext: ctx,
			storeRawOfficeUid,
		});
	}
	function stepContextActiveOrPrimary(): NotaryMenuOfficeIdentity | null {
		return tryActiveThenPrimaryOfficeIdentity(ctx, params.availableOffices, storeRawOfficeUid);
	}

	const hit = firstResolvedMenuIdentity([stepRealOffice, stepPreferredOffice, stepContextActiveOrPrimary]);
	if (hit) {
		return hit;
	}
	return {
		menuOfficeUid: "",
		menuRoleInOffice: ctx?.officeRole,
		storeRawOfficeUid,
	};
}
